// Convite gentil para conhecer uma aba (0.71, texto 09 Parte J). Reutilizável em qualquer aba.
// Quem participa de um grupo sem ter a aba vê os botões normais (editar, adicionar, cronômetro...). Ao tocar num deles:
//   · na 1ª vez da sessão do app (por aba), aparece a mensagem positiva com "Conhecer a aba" e "Agora não";
//   · depois disso, nada de mensagem: só um ✨ discreto ao lado do botão tocado.
// Nunca fala em "bloqueado" nem em "pagante".
import { useState, useSyncExternalStore } from 'react';
import { Alert, Modal, Platform, Text } from 'react-native';
import type { AbaId } from '../store/types';
import Paywall from '../screens/Paywall';
import { aba as abaDe } from './theme';
import { t } from '../lib/i18n';

// vale para a sessão do app (zera quando o app é fechado de verdade)
const mostrou = new Set<string>();
const tocados = new Set<string>();
let versao = 0;
const ouvintes = new Set<() => void>();
const avisar = () => { versao++; ouvintes.forEach(f => f()); };

/** Texto da mensagem de cada aba (as outras abas acrescentam o seu aqui). */
function mensagem(id: AbaId): { titulo: string; texto: string } {
  if (id === 'estudos') return { titulo: t('Quer estudar com tudo isso?'),
    texto: t('Com a aba Estudos você cria seus fichários, usa o cronômetro, acompanha seu relatório e muito mais. ✨') };
  const a = abaDe(id);
  return { titulo: t('Quer usar tudo isso?'), texto: t('Com a aba {aba} você cria o que é seu e usa todos os recursos. ✨', { aba: a?.nome ?? '' }) };
}

/**
 * useConvite('estudos'):
 *  - pedir('editar') no toque de um botão que só quem tem a aba usa;
 *  - brilho('editar') devolve ' ✨' depois que aquele botão já foi tocado (para pôr ao lado do rótulo);
 *  - conhecer / fecharConhecer: a tela da aba (screens/Paywall) foi pedida. Em tela cheia (sem janela por baixo),
 *    mostre <Paywall /> no lugar do conteúdo; dentro de uma janela, use "tela" (abre por cima, numa janela própria).
 */
export function useConvite(id: AbaId) {
  useSyncExternalStore(cb => { ouvintes.add(cb); return () => ouvintes.delete(cb); }, () => versao);
  const [conhecer, setConhecer] = useState(false);
  const pedir = (botao: string) => {
    const k = `${id}:${botao}`;
    const primeira = !mostrou.has(id);
    if (!tocados.has(k)) { tocados.add(k); avisar(); }
    if (!primeira) return;
    mostrou.add(id);
    const { titulo, texto } = mensagem(id);
    if (Platform.OS === 'web') {
      // no navegador, a janela do sistema com OK = "Conhecer a aba" e Cancelar = "Agora não"
      // eslint-disable-next-line no-alert
      if (typeof window !== 'undefined' && window.confirm(`${titulo}\n\n${texto}\n\n${t('OK = Conhecer a aba · Cancelar = Agora não')}`)) setConhecer(true);
      return;
    }
    Alert.alert(titulo, texto, [
      { text: t('Agora não'), style: 'cancel' },
      { text: t('Conhecer a aba'), onPress: () => setConhecer(true) },
    ], { cancelable: true });
  };
  const brilho = (botao: string) => (tocados.has(`${id}:${botao}`) && mostrou.has(id) ? ' ✨' : '');
  const fecharConhecer = () => setConhecer(false);
  const tela = conhecer ? (
    <Modal visible animationType="slide" onRequestClose={fecharConhecer}>
      <Paywall id={id} onBack={fecharConhecer} />
    </Modal>
  ) : null;
  return { pedir, brilho, conhecer, fecharConhecer, tela };
}

/** ✨ discreto ao lado de um botão já tocado (para botões que não são texto). */
export function Brilho({ on }: { on: boolean }) {
  return on ? <Text style={{ fontSize: 11, marginLeft: 2 }}>✨</Text> : null;
}
